import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { FiChevronDown, FiMenu, FiX, FiExternalLink } from 'react-icons/fi';
import siteData from '../data/siteData';

const NAV_ITEMS = [
  { key: 'Home',         label: 'Home',          to: '/',         type: 'link' },
  { key: 'Team',         label: 'Our Team',       to: '/team',     type: 'link' },
  { key: 'Services',     label: 'Services',       to: '/services', type: 'dropdown' },
  { key: 'Knowledge Bank', label: 'Knowledge Bank', to: '#',       type: 'dropdown' },
  { key: 'Blogs',        label: 'Blogs',          to: '/blogs',    type: 'link' },
  { key: 'Query',        label: 'Query',          to: '/query',    type: 'link' },
  { key: 'Careers',      label: 'Careers',        to: '/careers',  type: 'link' },
  { key: 'Contact Us',   label: 'Contact',        to: '/contact',  type: 'link' },
];

export default function Navbar() {
  const [scrolled, setScrolled]     = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDrop, setOpenDrop]     = useState(null);
  const navRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setMobileOpen(false);
    setOpenDrop(null);
  }, [location]);

  // Click outside closes desktop dropdowns
  useEffect(() => {
    const handler = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenDrop(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const renderDesktopNav = () => (
    <nav className="navbar-desktop-nav">
      {NAV_ITEMS.map((item) => {
        if (item.type === 'link') {
          return (
            <NavLink
              key={item.key}
              to={item.to}
              className={({ isActive }) =>
                `navbar-nav-link ${isActive ? 'active' : ''}`
              }
            >
              {item.label}
            </NavLink>
          );
        }

        if (item.type === 'ext') {
          return (
            <a key={item.key} href={item.href} target="_blank" rel="noopener noreferrer" className="navbar-nav-link">
              {item.label} <FiExternalLink size={11} />
            </a>
          );
        }

        if (item.type === 'dropdown') {
          const dropItems = siteData.navigation_bar[item.key]?.dropdown_items || [];
          return (
            <div
              key={item.key}
              className="navbar-drop-wrapper"
              onMouseEnter={() => setOpenDrop(item.key)}
              onMouseLeave={() => setOpenDrop(null)}
            >
              <NavLink
                to={item.to}
                className={({ isActive }) =>
                  `navbar-nav-link navbar-has-drop ${isActive && item.to !== '#' ? 'active' : ''}`
                }
                onClick={(e) => e.preventDefault()}
              >
                {item.label} <FiChevronDown size={14} className={`navbar-caret ${openDrop === item.key ? 'navbar-caret-open' : ''}`} />
              </NavLink>
              <div className={`navbar-dropdown ${openDrop === item.key ? 'navbar-drop-open' : ''}`}>
                {dropItems.map((s) => {
                  const isInternal = s.link.startsWith('/');
                  return isInternal ? (
                    <Link key={s.label} to={s.link} className="navbar-drop-item">
                      {s.label}
                    </Link>
                  ) : (
                    <a key={s.label} href={s.link} target="_blank" rel="noopener noreferrer" className="navbar-drop-item">
                      {s.label}
                    </a>
                  );
                })}
              </div>
            </div>
          );
        }

        return null;
      })}
    </nav>
  );

  return (
    <header
      ref={navRef}
      className={`navbar-header ${scrolled ? 'scrolled' : ''}`}
    >
      {/* ── Desktop Top Row (Logos Only) ── */}
      <div className="navbar-top-row">
        <div className="container navbar-top-inner">
          <Link to="/" className="navbar-logo">
            <img src="/images/logo/logo.png" alt="CA India" className="navbar-logo-img" />
            <div className="navbar-logo-text">
              <span className="navbar-logo-main">ASRM & Co.</span>
              <span className="navbar-logo-sub">Chartered Accountants</span>
            </div>
          </Link>
          <div className="navbar-top-right">
            <img src="/images/logo/logo-m.png" alt="ASRM Logo" className="navbar-right-logo" />
          </div>
        </div>
      </div>

      {/* ── Desktop Bottom Row (Navigation Items Only) ── */}
      <div className="navbar-bottom-row">
        <div className="container navbar-bottom-inner">
          {renderDesktopNav()}

        </div>
      </div>

      {/* ── Mobile/Tablet Single-Row Header ── */}
      <div className="container navbar-inner">
        <Link to="/" className="navbar-logo">
          <img src="/images/logo/logo.png" alt="CA India" className="navbar-logo-img" />
          <div className="navbar-logo-text">
            <span className="navbar-logo-main">ASRM & Co.</span>
            <span className="navbar-logo-sub">Chartered Accountants</span>
          </div>
        </Link>
        <div className="navbar-actions">
          <button
            className="navbar-hamburger"
            onClick={() => setMobileOpen((p) => !p)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <FiX size={22} /> : <FiMenu size={22} />}
          </button>
        </div>
      </div>

      {/* ── Mobile Menu ── */}
      <div className={`navbar-mobile-menu ${mobileOpen ? 'navbar-mobile-open' : ''}`}>
        <nav className="navbar-mobile-nav">
          <NavLink to="/" className="navbar-mobile-link" onClick={() => setMobileOpen(false)}>Home</NavLink>
          <NavLink to="/team" className="navbar-mobile-link" onClick={() => setMobileOpen(false)}>Our Team</NavLink>
          <NavLink to="/services" className="navbar-mobile-link" onClick={() => setMobileOpen(false)}>Services</NavLink>

          <NavLink to="/knowledge" className="navbar-mobile-link" onClick={() => setMobileOpen(false)}>Knowledge Bank</NavLink>

          <NavLink to="/blogs" className="navbar-mobile-link" onClick={() => setMobileOpen(false)}>Blogs</NavLink>
          <NavLink to="/query" className="navbar-mobile-link" onClick={() => setMobileOpen(false)}>Query</NavLink>
          <NavLink to="/careers" className="navbar-mobile-link" onClick={() => setMobileOpen(false)}>Careers</NavLink>
          <NavLink to="/contact" className="navbar-mobile-link" onClick={() => setMobileOpen(false)}>Contact</NavLink>
        </nav>
      </div>
    </header>
  );
}
